import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { isUndefined, reject, filter, take, isEqual, sortBy as sortByLodash } from 'lodash-es';

import ThCell from './components/ThCell';
import THead from './components/THead';
import TBody from './components/TBody';
import SubRows from './components/SubRows';
import ExpandableRow from './components/ExpandableRow';
import ShowMoreCell from './components/ShowMoreCell';

let that;

class SortableTable extends Component {
  static propTypes = {
    onShowMore: PropTypes.func,
    onSortEnd: PropTypes.func,
    onRowClick: PropTypes.func,
    rows: PropTypes.array.isRequired,
    columns: PropTypes.arrayOf(
      PropTypes.shape({
        sortable: PropTypes.bool,
        text: PropTypes.string,
        sortBy: PropTypes.string,
        sortOrder: PropTypes.string,
        tooltipText: PropTypes.string,
        style: PropTypes.object,
        dataField: PropTypes.string.isRequired,
        className: PropTypes.string,
        element: PropTypes.element,
      })
    ),
    sortOrder: PropTypes.string,
    defaultSortBy: PropTypes.string,
    sortParams: PropTypes.shape({
      by: PropTypes.string,
      order: PropTypes.string,
    }),
    noParentUpdate: PropTypes.bool,
    initialRowsLimit: PropTypes.number,
    shouldRenderColumns: PropTypes.bool,
    tableStaticHeaderRows: PropTypes.element,
    isTooltipFirst: PropTypes.bool,
    hasShowMoreCell: PropTypes.bool,
    showMoreCount: PropTypes.number,
    showMoreCellColSpan: PropTypes.number,
    showMoreCellSimplified: PropTypes.bool,
    emptyStateText: PropTypes.any,
    showEmptyCells: PropTypes.bool,
    emptyCellComponent: PropTypes.object,
    animatedTable: PropTypes.bool,
    animatedTableProps: PropTypes.object,
    infiniteScrollTable: PropTypes.bool,
    infiniteScrollProps: PropTypes.object,
    tableClassName: PropTypes.string,
    tableId: PropTypes.string,
    theadClassName: PropTypes.string,
    tbodyClassName: PropTypes.string,
    headerElTopStyle: PropTypes.object,
    style: PropTypes.object,
    expandedRows: PropTypes.array,
    expandedRowsMaxHeight: PropTypes.number,
  };
  static defaultProps = {
    onRowClick: () => {},
    noParentUpdate: false,
    initialRowsLimit: 10,
    showEmptyCells: true,
    showMoreCount: 10,
    hasShowMoreCell: true,
    showMoreCellColSpan: 6,
    showMoreCellSimplified: false,
    animatedTable: false,
    infiniteScrollTable: false,
    tableClassName: 'table',
    headerElTopStyle: { top: 0, width: '100%' },
    tableId: '',
    expandedRows: [],
  };
  static getDerivedStateFromProps(props, state) {
    if (
      isEqual(props.staticHeaderRows, state.prevPropsStaticHeaderRows) &&
      isEqual(props.columns, state.prevPropsColumns) &&
      isEqual(props.rows, state.prevPropsRows)
    ) {
      return null;
    }

    const moreCount = that.initialValueOfShowMore(props.rows, state.rowsLimit);

    return {
      staticHeaderRows: props.staticHeaderRows,
      prevPropsStaticHeaderRows: props.staticHeaderRows,
      columns: props.columns,
      prevPropsColumns: props.columns,
      rows: props.rows,
      prevPropsRows: props.rows,
      moreCount,
      prevPropsMoreCount: moreCount,
    };
  }
  constructor(props) {
    super(props);
    that = this;
    const moreCount = this.initialValueOfShowMore(this.props.rows, this.props.initialRowsLimit);
    this.state = {
      sortBy: props.defaultSortBy,
      sortOrder: isUndefined(props.sortOrder) ? 'desc' : props.sortOrder,
      rows: this.props.rows,
      prevPropsStateRows: this.props.rows,
      rowsLimit: props.initialRowsLimit,
      moreCount,
      prevPropsMoreCount: moreCount,
    };
  }
  shouldComponentUpdate(nextProps, nextState) {
    if (!this.props.noParentUpdate) {
      return true;
    }
    const { sortBy, sortOrder, rowsLimit } = this.state;
    if (
      nextState.sortBy === sortBy &&
      nextState.sortOrder === sortOrder &&
      nextState.rowsLimit === rowsLimit
    ) {
      return false;
    }
    return true;
  }

  componentDidUpdate(prevProps) {
    const { sortParams } = this.props;

    if (sortParams && !isEqual(prevProps.sortParams, sortParams)) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ sortBy: sortParams.by, sortOrder: sortParams.order });
    }
  }

  get isTableLayout() {
    return !this.props.infiniteScrollTable && !this.props.animatedTable;
  }
  handleSort = (sortBy, sortOrder) => {
    if (this.props.onSortEnd) {
      this.setState({ sortBy, sortOrder }, () =>
        this.props.onSortEnd({ sortBy, sortOrder, rows: this.state.rows })
      );
    } else {
      this.setState({ sortBy, sortOrder, rows: this.sortedRows() });
    }
  };
  sortedRows = () => {
    const { sortBy, sortOrder } = this.state;
    if (this.props.onSortEnd) {
      return this.state.rows;
    }
    let rows = sortByLodash(
      reject(this.state.rows, (r) => isUndefined(r[sortBy])),
      sortBy
    );
    if (sortOrder === 'desc') {
      rows = rows.reverse();
    }
    rows = rows.concat(filter(this.state.rows, (r) => isUndefined(r[sortBy])));
    return rows;
  };
  initialValueOfShowMore = (allRows, rowsLimit) => {
    const { initialRowsLimit, showMoreCount } = this.props;
    if (allRows.length === initialRowsLimit) {
      return 0;
    }

    if (allRows.length === rowsLimit) {
      return 0;
    }

    const rowsCountLeft = allRows.length - initialRowsLimit;
    return rowsCountLeft >= showMoreCount ? showMoreCount : rowsCountLeft;
  };
  handleShowMore = (count) => {
    const newLimit = this.state.rowsLimit + count;
    const rowsCountLeft = this.state.rows.length - newLimit;
    const moreCount =
      rowsCountLeft >= this.props.showMoreCount ? this.props.showMoreCount : rowsCountLeft;
    this.setState({ rowsLimit: newLimit, moreCount });
  };

  thColumns() {
    const { sortBy, sortOrder } = this.state;
    return this.props.columns.map((column, i) => {
      const renderKey = `${column.dataField}-${i}`;
      const sortOrderValue = sortOrder !== this.props.sortOrder ? sortOrder : this.props.sortOrder;
      const sort = column.sortBy === sortBy ? sortOrderValue : '';
      return (
        <ThCell
          key={renderKey}
          className={column.className}
          colSpan={column.colSpan}
          style={column.style}
          text={column.text}
          sortable={column.sortable}
          sortOrder={sort}
          sortBy={column.sortBy}
          columnSortOrder={column.sortOrder}
          tooltipText={column.tooltipText}
          isTooltipFirst={column.isTooltipFirst}
          element={column.element}
          isTableLayout={this.isTableLayout}
          entireCellTooltip={column.entireCellTooltip}
          onSort={this.handleSort}
        />
      );
    });
  }

  tbodyRows() {
    const { rowsLimit } = this.state;
    const {
      onRowClick,
      onShowMore,
      columns,
      showEmptyCells,
      emptyCellComponent,
      initialRowsLimit,
    } = this.props;

    const updatedRowsLimit = rowsLimit >= initialRowsLimit ? rowsLimit : initialRowsLimit;
    const rowsToDisplay = onShowMore
      ? this.sortedRows()
      : take(this.sortedRows(), updatedRowsLimit);

    return rowsToDisplay.map((row, i) => {
      const tds = columns.map((column, j) => {
        const renderKey = `${i}-${j}`;
        const cell = row[`${column.dataField}_display`] || row[column.dataField];

        if (!cell) {
          if (!showEmptyCells) return <Fragment key={renderKey} />;
          if (showEmptyCells) {
            return this.isTableLayout ? (
              <td key={renderKey} className="empty-cell">
                {emptyCellComponent}
              </td>
            ) : (
              <div key={renderKey} className="d-table-cell d-td">
                {emptyCellComponent}
              </div>
            );
          }
        }

        const key = `${cell}+${column.dataField}`;
        const cellProps = row[`${column.dataField}_td_props`] || {};
        if (!this.isTableLayout) {
          return (
            <div key={key} className="d-table-cell d-td" {...cellProps}>
              <span>{cell}</span>
            </div>
          );
        }

        const isFragment = cell.type && cell.type.toString() === Fragment.toString();
        return cell.type === 'td' || isFragment ? (
          <Fragment key={key}>{cell}</Fragment>
        ) : (
          <td key={key} {...cellProps}>
            {cell}
          </td>
        );
      });

      if (this.isTableLayout) {
        if (row.canExpand) {
          const isExpanded = this.props.expandedRows.includes(row.key);

          return (
            <Fragment key={row.key || i}>
              <tr
                className={cx('expandable-head', row.rowClassName, isExpanded && 'expanded')}
                onClick={(e) => onRowClick(e, i, row.key)}
              >
                {tds}
              </tr>
              <ExpandableRow
                show={isExpanded}
                rootCellColSpan={columns.length}
                transitionTimeout={row.transitionTimeout}
              >
                <SubRows row={row} columns={columns} maxHeight={this.props.expandedRowsMaxHeight} />
              </ExpandableRow>
            </Fragment>
          );
        }

        return (
          <tr
            key={row.key || i}
            className={row.rowClassName}
            onClick={(e) => onRowClick(e, i, row.key)}
          >
            {tds}
          </tr>
        );
      }

      return (
        <div
          key={row.key || i}
          className={cx('d-tr', row.rowClassName)}
          onClick={(e) => onRowClick(e, i, row.key)}
        >
          {tds}
        </div>
      );
    });
  }

  render() {
    const {
      tableStaticHeaderRows,
      shouldRenderColumns,
      emptyStateText,
      onShowMore,
      showMoreCount,
      hasShowMoreCell,
      showMoreCellColSpan,
      showMoreCellSimplified,
      animatedTable,
      animatedTableProps,
      infiniteScrollTable,
      infiniteScrollProps,
      tableClassName,
      theadClassName,
      tbodyClassName,
      headerElTopStyle,
      style,
      tableId,
    } = this.props;
    const tHead = (
      <THead
        isTableLayout={this.isTableLayout}
        thCells={this.thColumns()}
        staticRows={tableStaticHeaderRows}
        className={theadClassName}
        style={headerElTopStyle}
        shouldRenderRow={shouldRenderColumns}
      />
    );

    const tBody = (
      <TBody
        tableRows={this.tbodyRows()}
        isTableLayout={this.isTableLayout}
        isAnimatedTable={animatedTable}
        isInfiniteScroll={infiniteScrollTable}
        animatedTableProps={animatedTableProps}
        infiniteScrollProps={infiniteScrollProps}
        emptyCellColSpan={showMoreCellColSpan}
        emptyState={emptyStateText}
        className={tbodyClassName}
        style={{ width: headerElTopStyle.width }}
      />
    );

    const showMore = (
      <ShowMoreCell
        colSpan={showMoreCellColSpan}
        moreCount={onShowMore ? showMoreCount : this.state.moreCount}
        showMoreCellSimplified={showMoreCellSimplified}
        onShowMore={onShowMore || this.handleShowMore}
      />
    );

    return (
      <Fragment>
        {this.isTableLayout && (
          <table className={tableClassName} style={style} id={tableId}>
            {tHead}
            {tBody}
            {hasShowMoreCell ? <tfoot>{showMore}</tfoot> : null}
          </table>
        )}
        {infiniteScrollTable && (
          <div
            ref={infiniteScrollProps.boxElRef}
            className="show-scrollbar-box"
            style={{ width: headerElTopStyle.width }}
          >
            <div className={cx('d-table', tableClassName)} style={style}>
              {tHead}
              {tBody}
            </div>
          </div>
        )}
        {animatedTable && (
          <div className={cx('d-table', tableClassName)} style={style}>
            {tHead}
            {tBody}
            {hasShowMoreCell && <div className={cx('d-table')}>{showMore}</div>}
          </div>
        )}
      </Fragment>
    );
  }
}

export default SortableTable;
